<template>
  <div>
    <v-card class="mb-4" elevation="0">
      <div class="d-flex justify-space-between align-center">
        <div>
          <v-card-title class="text-subtitle-1 text-sm-h5 font-weight-bold px-0 pt-0 pb-1">
            수능/모의고사 일정
          </v-card-title>
          <v-card-subtitle class="px-0 text-caption text-sm-body-2">
            {{ selectedYear }}년 수험생 주요 일정
          </v-card-subtitle>
        </div>
        <div style="width: 110px; min-width: 110px;">
          <v-select
            v-model="selectedYear"
            :items="availableYears"
            label="연도"
            variant="outlined"
            density="compact"
            hide-details
            color="primary"
            class="year-select"
          ></v-select>
        </div>
      </div>
    </v-card>

    <!-- 1. Main CSAT Card (Mobile Optimized) -->
    <v-card 
      v-if="displayEvent"
      class="mb-6 overflow-hidden"
      elevation="2"
      border
      rounded="xl"
      color="primary"
      variant="flat"
      @click="!id ? $router.push(`/csat/${displayEvent.id}`) : null"
    >
      <v-row no-gutters>
        <!-- Left: Info -->
        <v-col cols="12" sm="8" class="pa-4 pa-sm-6">
          <div class="text-h4 text-sm-h3 font-weight-black text-white mb-1 mb-sm-2">
            {{ calculateDDay(displayEvent.date) }}
          </div>
          <div class="text-subtitle-1 text-sm-h5 font-weight-bold text-white mb-1 mb-sm-2 text-wrap" style="word-break: keep-all;">
            {{ displayEvent.title }}
          </div>
          <div class="d-flex align-center text-caption text-sm-subtitle-1 text-blue-lighten-4">
            <v-icon icon="mdi-calendar-clock" size="x-small" class="mr-1 mr-sm-2"></v-icon>
            {{ formatDate(displayEvent.date) }}
          </div>
        </v-col>
        
        <!-- Right: Cheer Icon -->
        <v-col cols="12" sm="4" class="d-flex align-center justify-center pa-3 pa-sm-6 bg-blue-darken-3 border-sm-left">
          <div class="d-flex flex-row flex-sm-column align-center">
            <v-icon icon="mdi-school" size="32" size-sm="64" color="blue-lighten-4" class="mr-3 mr-sm-0 mb-sm-2"></v-icon>
            <div class="text-subtitle-2 text-sm-h6 font-weight-bold text-white text-center">
              수험생 여러분을<br class="d-none d-sm-block"> 응원합니다 :)
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-alert v-else type="warning" variant="tonal" class="mb-6" rounded="lg">
      선택한 일정이 존재하지 않거나 연도가 맞지 않습니다.
    </v-alert>

    <!-- 2. Progress Gauge -->
    <v-card class="mb-8 pa-4 pa-sm-5" border elevation="1" rounded="lg" v-if="displayEvent">
      <div class="d-flex justify-space-between align-end mb-2">
        <div>
          <div class="text-body-2 text-sm-subtitle-1 font-weight-bold text-grey-darken-3">목표를 향한 <span class="font-italic text-primary">완주율</span></div>
          <div class="text-caption text-medium-emphasis d-none d-sm-block">
             지난 수능 다음 날부터 시작된 여정입니다.
          </div>
        </div>
        <div class="text-h6 text-sm-h5 font-weight-black text-primary">
          {{ progressPercentage }}%
        </div>
      </div>
      <v-progress-linear
        :model-value="progressPercentage"
        color="primary"
        height="8"
        height-sm="12"
        rounded
        bg-color="blue-lighten-4"
      >
      </v-progress-linear>
    </v-card>

    <!-- 1-1. Detailed Info (Now below Progress Gauge) -->
    <v-card v-show="displayEvent" class="mb-6 pa-4 pa-sm-6" border elevation="1" rounded="xl">
      <div class="d-flex flex-wrap align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <v-icon icon="mdi-information-outline" color="primary" class="mr-2"></v-icon>
          <h3 class="text-h6 font-weight-bold text-grey-darken-3">시험 상세 정보</h3>
        </div>
        <v-chip color="info" size="small" label class="font-weight-bold" v-if="displayEvent">
          {{ displayEvent.target || '전체' }} 대상
        </v-chip>
      </div>
      
      <v-row v-if="displayEvent">
        <v-col cols="12" class="py-2">
          <div class="text-caption text-medium-emphasis mb-1">주최 기관</div>
          <div class="text-body-1 font-weight-bold" style="word-break: keep-all;">
            <v-icon icon="mdi-office-building" size="small" class="mr-1"></v-icon>
            {{ getHostInfo(displayEvent) }}
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-caption text-medium-emphasis">시험 시간표 (일반 수험생 기준)</div>
        <v-btn
          :prepend-icon="showSchedule ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          variant="tonal"
          size="x-small"
          color="primary"
          @click="showSchedule = !showSchedule"
        >
          {{ showSchedule ? '시간표 숨기기' : '시간표 보기' }}
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="showSchedule">
          <v-table density="comfortable" class="time-table border rounded-lg">
            <thead>
              <tr class="bg-grey-lighten-4">
                <th class="text-center font-weight-bold">교시</th>
                <th class="text-center font-weight-bold">시험 영역</th>
                <th class="text-center font-weight-bold">시험 시간</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(schedule, index) in examSchedule" :key="index" :class="{'bg-blue-lighten-5': schedule.period === '점심'}">
                <td class="text-center text-caption font-weight-bold">{{ schedule.period }}</td>
                <td class="text-center font-weight-medium text-body-2">{{ schedule.subject }}</td>
                <td class="text-center text-caption">{{ schedule.time }}</td>
              </tr>
            </tbody>
          </v-table>
          <div class="mt-3 text-caption text-error font-weight-medium">
            * 실제 시험 당일 상황에 따라 시간이 다소 변동될 수 있습니다.
          </div>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- 3. CSAT Events List (Only shown on Mock test detail page) -->
    <template v-if="id && displayEvent && !isMainCsat(displayEvent)">
      <div class="d-flex align-center mb-4">
        <v-icon icon="mdi-school" color="primary" class="mr-2"></v-icon>
        <h3 class="text-h6 font-weight-bold text-grey-darken-3">수능 일정</h3>
      </div>
      <v-row class="mb-4">
        <v-col 
          v-for="event in allCsatEvents" 
          :key="event.id" 
          cols="12" 
          sm="6" 
          md="4"
        >
          <v-card 
            class="h-100" 
            hover 
            elevation="1"
            @click="$router.push(`/csat/${event.id}`)"
            rounded="lg"
            style="border-top: 4px solid;"
            :style="{ borderTopColor: Number(id) === event.id ? '#FFAB00' : '#1976D2' }" 
          >
            <v-card-item>
              <div class="d-flex justify-space-between align-start mb-2">
                <div class="d-flex flex-wrap gap-1">
                  <v-chip color="primary" variant="flat" size="x-small" label class="font-weight-bold mr-1">
                    수능
                  </v-chip>
                  <v-chip v-if="event.target" color="info" variant="flat" size="x-small" label>
                    {{ event.target }}
                  </v-chip>
                </div>
                <span class="text-subtitle-2 font-weight-bold text-primary">
                  {{ calculateDDay(event.date) }}
                </span>
              </div>
              
              <v-card-title class="text-h6 font-weight-bold pb-1 text-wrap" style="line-height: 1.2; word-break: keep-all;">
                {{ event.title }}
              </v-card-title>
              
              <div class="d-flex align-center mt-2 text-caption text-medium-emphasis font-weight-medium">
                <v-icon icon="mdi-calendar" size="x-small" class="mr-1"></v-icon>
                {{ formatDate(event.date) }}
              </div>
            </v-card-item>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- 4. Mock Test List (Always Visible) -->
    <div class="d-flex align-center mb-4">
      <v-icon icon="mdi-pencil-box-multiple" color="primary" class="mr-2"></v-icon>
      <h3 class="text-h6 font-weight-bold text-grey-darken-3">모의고사 일정</h3>
    </div>

    <v-row>
      <v-col 
        v-for="event in mockTestEvents" 
        :key="event.id" 
        cols="12" 
        sm="6" 
        md="4"
      >
        <v-card 
          class="h-100" 
          hover 
          elevation="1"
          @click="$router.push(`/csat/${event.id}`)"
          rounded="lg"
          style="border-top: 4px solid;"
          :style="{ borderTopColor: Number(id) === event.id ? '#FFAB00' : '#1976D2' }" 
        >
          <v-card-item>
            <div class="d-flex justify-space-between align-start mb-2">
              <div class="d-flex flex-wrap gap-1">
                <v-chip color="primary" variant="tonal" size="x-small" label class="font-weight-bold mr-1">
                  모의고사
                </v-chip>
                <v-chip v-if="event.target" color="info" variant="flat" size="x-small" label>
                  {{ event.target }}
                </v-chip>
              </div>
              <span class="text-subtitle-2 font-weight-bold text-primary">
                {{ calculateDDay(event.date) }}
              </span>
            </div>
            
            <v-card-title class="text-h6 font-weight-bold pb-1 text-wrap" style="line-height: 1.2; word-break: keep-all;">
              {{ event.title }}
            </v-card-title>
            
            <div class="d-flex align-center mt-2 text-caption text-medium-emphasis font-weight-medium">
              <v-icon icon="mdi-calendar" size="x-small" class="mr-1"></v-icon>
              {{ formatDate(event.date) }}
            </div>
          </v-card-item>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { events } from '../data/events';
import { formatDate, calculateDDay } from '../assets/globalFx';

export default {
  name: 'CsatPage',
  props: ['id'],
  data() {
    return {
      selectedEvent: null,
      events: events,
      selectedYear: 2026,
      availableYears: [2025, 2026, 2027],
      now: new Date(),
      timer: null,
      showSchedule: false
    }
  },
  watch: {
    // id가 바뀔 때(또는 없어질 때) 해당 이벤트의 연도로 자동 전환
    displayEvent: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const year = new Date(newVal.date).getFullYear();
          if (this.availableYears.includes(year)) {
            this.selectedYear = year;
          }
        }
      }
    }
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 50);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  computed: {
    currentYearEvents() {
      return this.events.filter(event => {
        const eventYear = new Date(event.date).getFullYear();
        return event.categoryId === 'csat' && eventYear === this.selectedYear;
      });
    },
    mainCsatEvent() {
      return this.currentYearEvents.find(e => this.isMainCsat(e));
    },
    allCsatEvents() {
      return this.currentYearEvents
        .filter(e => this.isMainCsat(e))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    displayEvent() {
      if (this.id) {
        // ID가 일치하면서 카테고리가 'csat'인 이벤트만 반환
        return this.events.find(e => e.id === Number(this.id) && e.categoryId === 'csat');
      }
      return this.mainCsatEvent;
    },
    mockTestEvents() {
      return this.currentYearEvents
        .filter(e => !this.isMainCsat(e))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    examSchedule() {
      return [
        { period: '1교시', subject: '국어', time: '08:40 ~ 10:00 (80분)' },
        { period: '2교시', subject: '수학', time: '10:30 ~ 12:10 (100분)' },
        { period: '점심', subject: '중식 시간', time: '12:10 ~ 13:00 (50분)' },
        { period: '3교시', subject: '영어', time: '13:10 ~ 14:20 (70분)' },
        { period: '4교시', subject: '한국사', time: '14:50 ~ 15:20 (30분)' },
        { period: '-', subject: '시험지 회수/배부', time: '15:20 ~ 15:35 (15분)' },
        { period: '4교시', subject: '사회/과학 탐구', time: '15:35 ~ 16:37 (62분)' },
        { period: '5교시', subject: '제2외국어/한문', time: '17:05 ~ 17:45 (40분)' },
      ];
    },
    progressPercentage() {
      if (!this.displayEvent) return 0;

      const targetDate = new Date(this.displayEvent.date);
      targetDate.setHours(0, 0, 0, 0);

      // 모든 시험(수능/모의고사)의 시작 날짜를 '작년 수능 다음 날'로 통일
      const prevYearCsat = this.events.find(e => {
        const d = new Date(e.date);
        return this.isMainCsat(e) && d.getFullYear() === this.selectedYear - 1;
      });

      let startDate;
      if (prevYearCsat) {
        startDate = new Date(prevYearCsat.date);
        startDate.setDate(startDate.getDate() + 1);
      } else {
        // 작년 데이터가 없을 경우에만 해당 연도 1월 1일 등으로 대체 (안전장치)
        startDate = new Date(targetDate.getFullYear() - 1, 10, 15); // 대략적인 작년 11월 중순
      }
      
      startDate.setHours(0, 0, 0, 0);

      const today = this.now;

      if (today < startDate) return 0;
      if (today > targetDate) return 100;

      const totalDuration = targetDate - startDate;
      const passedDuration = today - startDate;

      const percentage = (passedDuration / totalDuration) * 100;
      return Math.min(Math.max(percentage, 0), 100).toFixed(7);
    }
  },
  methods: {
    formatDate,
    calculateDDay,
    isMainCsat(event) {
        return event.title.includes('대학수학능력시험') && !event.title.includes('모의');
    },
    getHostInfo(event) {
      return event?.host || '각 시·도 교육청';
    }
  }
}
</script>

<style scoped>
/* 연도 선택 박스 모바일 최적화 */
.year-select :deep(.v-field__input) {
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 0.875rem;
}
</style>
